<template>
  <div class="card mt-3">
    <div class="card-body">
      <h5 class="card-title">
        {{ trans("admin.cdr.reassignmentExecution") }}
      </h5>

      <div v-if="execution.source_patient" class="mb-3">
        <button
          type="button"
          class="btn btn-link p-0 text-muted small text-decoration-none"
          :aria-expanded="showSourcePatient"
          @click="showSourcePatient = !showSourcePatient"
        >
          <i
            :class="
              showSourcePatient ? 'fal fa-chevron-down' : 'fal fa-chevron-right'
            "
          />
          {{ trans("admin.cdr.currentPatient") }}
        </button>
        <div v-if="showSourcePatient" class="card p-3 mt-2">
          <div class="row">
            <div class="col-md-6">
              <strong
                >{{ trans("admin.cdr.destinationPatientFirstname") }}:</strong
              >
              {{ execution.source_patient.firstname }}
            </div>
            <div class="col-md-6">
              <strong
                >{{ trans("admin.cdr.destinationPatientLastname") }}:</strong
              >
              {{ execution.source_patient.lastname }}
            </div>
          </div>
          <div class="row mt-2">
            <div class="col-md-6">
              <strong>{{ trans("admin.cdr.lookupSsn") }}:</strong>
              {{ execution.source_patient.ssn }}
            </div>
            <div v-if="execution.source_patient.date_of_birth" class="col-md-6">
              <strong>{{ trans("admin.cdr.destinationPatientDob") }}:</strong>
              {{ execution.source_patient.date_of_birth }}
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="execution.destination_patient_note"
        class="alert alert-info mb-3"
      >
        <strong>{{ trans("admin.cdr.destinationPatientNote") }}:</strong>
        {{ execution.destination_patient_note }}
      </div>

      <cdr-patient-selector
        :ssn-type="ssnType"
        :center-id="centerId"
        :gender-options="execution.gender_options"
        @patient="onPatientChange"
      />

      <div class="form-check mb-2">
        <input
          id="patient-verified"
          v-model="patientVerified"
          class="form-check-input"
          type="checkbox"
          :disabled="!destinationPatient"
        />
        <label class="form-check-label" for="patient-verified">
          {{ trans("admin.cdr.confirmPatientDetails") }}
        </label>
      </div>

      <div v-if="execution.scope" class="form-check mb-2">
        <input
          id="reassign-scope-acknowledge"
          v-model="scopeAcknowledged"
          class="form-check-input"
          type="checkbox"
        />
        <label class="form-check-label" for="reassign-scope-acknowledge">
          {{ execution.scope.label }}
        </label>
      </div>

      <CdrIntegrationAcknowledgements
        :transfers="execution.integration_transfers || []"
        @change="integrationAcks = $event"
      />

      <div class="form-check mb-3">
        <input
          id="reassign-acknowledge"
          v-model="acknowledged"
          class="form-check-input"
          type="checkbox"
        />
        <label class="form-check-label" for="reassign-acknowledge">
          {{ trans("admin.cdr.reassignmentAcknowledge") }}
        </label>
      </div>

      <button
        type="button"
        class="btn btn-warning"
        :disabled="!canSubmit || submitting"
        @click="submit"
      >
        {{ trans("admin.cdr.executeReassignment") }}
      </button>
    </div>
  </div>
</template>

<script>
import { practitionerHttpService } from "../../app/practitioner-portal/store/practitioner-http-service";
import CdrIntegrationAcknowledgements from "./CdrIntegrationAcknowledgements.vue";
import CdrPatientSelector from "./CdrPatientSelector.vue";
import { integrationAcksComplete } from "./cdr-integration-acks";

export default {
  components: { CdrIntegrationAcknowledgements, CdrPatientSelector },
  props: {
    flag: { type: Object, required: true },
    execution: { type: Object, required: true },
    apiUrl: { type: String, required: true },
  },
  emits: ["updated"],
  data() {
    return {
      acknowledged: false,
      scopeAcknowledged: false,
      patientVerified: false,
      showSourcePatient: false,
      submitting: false,
      destinationPatient: null,
      integrationAcks: [],
    };
  },
  computed: {
    canSubmit() {
      if (
        !this.acknowledged ||
        !this.patientVerified ||
        !this.destinationPatient
      )
        return false;
      if (this.execution.scope && !this.scopeAcknowledged) return false;
      return integrationAcksComplete(this.integrationAcks);
    },
    ssnType() {
      return this.flag.flaggable?.patient_ssn_type || "SSN_SE";
    },
    centerId() {
      return this.flag.flaggable?.center?.id;
    },
  },
  watch: {
    destinationPatient() {
      this.patientVerified = false;
      this.acknowledged = false;
      this.scopeAcknowledged = false;
    },
  },
  methods: {
    onPatientChange(patient) {
      this.destinationPatient = patient;
    },
    submit() {
      this.submitting = true;
      const patient = this.destinationPatient;
      const payload = {
        acknowledge: true,
        integration_acknowledgements: this.integrationAcks,
      };

      if (patient.id) {
        payload.destination_patient_id = patient.id;
      } else {
        payload.destination_patient = {
          ssn: patient.ssn,
          ssn_type: patient.ssn_type,
          firstname: patient.firstname,
          lastname: patient.lastname,
          date_of_birth: patient.date_of_birth,
          gender: patient.gender,
        };
      }

      practitionerHttpService
        .post(this.apiUrl + this.flag.id + "/execute-reassign", payload)
        .then((res) => {
          this.submitting = false;
          this.$emit("updated", res.data);
        })
        .catch((error) => {
          this.submitting = false;
          this.userErrorMessage(error);
        });
    },
  },
};
</script>
